import { Injectable, OnDestroy, RendererFactory2 } from '@angular/core';
import { UnsubscriberBase } from '@core/services/unsubscriber.base';
import { concatMap, switchMap, takeUntil } from 'rxjs/operators';
import { UserService } from '@app/services/user/user.service';
import { BehaviorSubject, of, timer } from 'rxjs';
import { AnalyticsEvents, AnalyticStatus, MixpanelService } from '@app/services/mixpanel.service';
import { FeatureFlagManagerService } from '@app/infrastructure/feature-flag-manager/feature-flag-manager.service';
import { FEATURE_FLAGS } from '@app/infrastructure/feature-flag-manager/feature-flag-list';

@Injectable()
export class ActivityTrackerService extends UnsubscriberBase implements OnDestroy {
	private readonly _fallbackTimerInSeconds: number = 60 * 20; // 20 minutes

	private _click$: BehaviorSubject<void> = new BehaviorSubject(null);

	constructor(
		private userService: UserService,
		private rendererFactory: RendererFactory2,
		private mixpanelService: MixpanelService,
		public featureFlagManagerService: FeatureFlagManagerService,
	) {
		super();
	}

	public init(): void {
		const renderer = this.rendererFactory.createRenderer(null, null);
		renderer.listen('document', 'click', () => this._click$.next());
		this.userService.isAuthenticated$
			.pipe(
				concatMap((isAuthenticated) => {
					if (isAuthenticated) {
						return this.userService.tokenLifetimeSeconds$;
					}
					return of(this._fallbackTimerInSeconds);
				}),
			)
			.subscribe((tokenLifetime) => {
				this.initTimer(tokenLifetime);
			});
	}

	private initTimer(tokenLifetimeInSeconds: number): void {
		this._click$
			.pipe(
				switchMap(() => timer(tokenLifetimeInSeconds * 1000)),
				takeUntil(this.unsubscribe),
			)
			.subscribe(() => {
				if (this.featureFlagManagerService.flags[FEATURE_FLAGS.ENABLE_OPTIMIZATION_HUB]) {
					this.mixpanelService.track(AnalyticsEvents.sessionOver, { Status: AnalyticStatus.timeOut });
				}
				this.userService.logout();
			});
	}
}
